import React, { useRef, useEffect, useState, useCallback } from 'react';
import './ResizablePanel.css';

const ResizablePanel = ({
  children,
  width,
  minWidth,
  maxWidth,
  visible,
  onResize,
  onDragStart,
  onDragEnd,
  resizeHandle = null,
  className = '',
  style = {},
  isChatOff = false
}) => {
  const panelRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startMouseX, setStartMouseX] = useState(0);
  const [startPanelWidth, setStartPanelWidth] = useState(0);

  // useRef로 드래그 상태 관리 (클로저 문제 해결)
  const dragRef = useRef({
    isDragging: false,
    startMouseX: 0,
    startPanelWidth: 0
  });

  const handleMouseMove = useCallback((e) => {
    if (!dragRef.current.isDragging) return;
    
    const deltaX = e.clientX - dragRef.current.startMouseX;
    let newWidth = dragRef.current.startPanelWidth + deltaX;
    
    // 최소/최대 폭 제한
    if (minWidth && newWidth < minWidth) newWidth = minWidth;
    if (maxWidth && newWidth > maxWidth) newWidth = maxWidth;
    
    // 패널 폭 즉시 업데이트
    if (panelRef.current) {
      panelRef.current.style.width = `${newWidth}px`;
    }
    
    // 리사이즈 콜백 호출
    onResize?.(newWidth);
  }, [minWidth, maxWidth, onResize]);

  const handleMouseUp = useCallback(() => {
    dragRef.current.isDragging = false;
    setIsDragging(false);
    onDragEnd?.();
    
    // 이벤트 리스너 제거
    document.removeEventListener('mousemove', handleMouseMove, { passive: false });
    document.removeEventListener('mouseup', handleMouseUp, { passive: false });
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [handleMouseMove, onDragEnd]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    
    // 드래그 상태 설정
    dragRef.current = {
      isDragging: true,
      startMouseX: e.clientX,
      startPanelWidth: panelRef.current.offsetWidth
    };
    
    setIsDragging(true);
    setStartMouseX(e.clientX);
    setStartPanelWidth(panelRef.current.offsetWidth);
    
    onDragStart?.();
    
    // 전역 이벤트 리스너 추가 (passive: false - preventDefault 사용)
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp, { passive: false });
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // width prop 변경 감지
  useEffect(() => {
    if (panelRef.current && !isDragging && width) {
      panelRef.current.style.width = typeof width === 'number' ? `${width}px` : width;
    }
  }, [width, isDragging]);

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, []);

  if (!visible) {
    return null;
  }

  const panelStyle = {
    ...(width && !isChatOff && { width: typeof width === 'number' ? `${width}px` : width }),
    ...(minWidth && { minWidth: `${minWidth}px` }),
    ...(maxWidth && !isChatOff && { maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth }),
    position: 'relative',
    height: '100%',
    flexShrink: (width && !isChatOff) ? 0 : 1, // chat이 켜져있고 width가 있으면 고정, 그 외엔 유연하게
    flexGrow: isChatOff ? 1 : 0, // chat이 꺼져있으면 남은 공간 차지
    boxSizing: 'border-box',
    ...style
  };

  return (
    <div
      ref={panelRef}
      className={`resizable-panel ${className} ${isDragging ? 'resizing' : ''}`}
      style={panelStyle}
    >
      {children}
      
      {/* 크기 조정 핸들 - resizeHandle이 설정된 경우에만 렌더링 */}
      {resizeHandle && onResize && (
        <div
          className={`resize-handle resize-handle-${resizeHandle}`}
          onMouseDown={handleMouseDown}
          style={{
            position: 'absolute',
            top: 0,
            [resizeHandle]: resizeHandle === 'right' ? '12px' : '-3px', // 조금 더 우측으로 이동
            width: '2px', // 매우 가늘게
            height: '100%',
            cursor: 'col-resize',
            zIndex: 1000,
            backgroundColor: isDragging ? 'rgba(0, 123, 255, 0.3)' : 'transparent'
          }}
          title="드래그하여 크기 조정"
        >
          <div 
            style={{
              width: '1px',
              height: '20px', // 표시기 길이 줄임
              backgroundColor: '#999', // 조금 더 진한 색
              margin: '0 auto',
              marginTop: '50%',
              opacity: isDragging ? 1 : 0,
              transition: 'opacity 0.2s'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ResizablePanel; 